/**
 * Modern Variant Picker - Web Components Implementation
 * Syncs with Splide image gallery and handles variant selection
 */

// ===================================================================
// Variant Selection Web Component
// ===================================================================
class VariantSelection extends HTMLElement {
  static get observedAttributes() {
    return ['variant', 'product-url'];
  }

  constructor() {
    super();
    this._product = null;
    this._productUrl = '';
    this._currentVariantId = null;
    this._masterSelect = null;
    this._initialized = false;
    this._productPromise = null;
    this._updatingFromGallery = false;
  }

  connectedCallback() {
    this._masterSelect = this.querySelector('select[name="id"][data-variants]');
    this._productUrl = this.getAttribute('product-url');
    
    if (this._masterSelect) {
      this._masterSelect.addEventListener('change', this._handleMasterSelectChange.bind(this));
    }

    // Load product data
    this._loadProductData();
    
    // Handle URL variant parameter on load
    this._handleUrlVariant();
    
    this._initialized = true;
  }

  disconnectedCallback() {
    if (this._masterSelect) {
      this._masterSelect.removeEventListener('change', this._handleMasterSelectChange.bind(this));
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this._initialized) return;

    if (name === 'variant' && oldValue !== newValue) {
      this._currentVariantId = newValue;

      // Wait for product data to be loaded before updating gallery
      if (this._product) {
        this._updateGallery(newValue);
      } else {
        this._loadProductData().then(() => {
          this._updateGallery(newValue);
        });
      }

      this._updateUrl(newValue);

      // Dispatch variant-change event for empire.js compatibility
      this._dispatchVariantChange(newValue);
    }
  }

  async _loadProductData() {
    if (!this._productUrl) return;

    // If already loading, return existing promise
    if (this._productPromise) return this._productPromise;

    this._productPromise = (async () => {
      try {
        const response = await fetch(this._productUrl);
        this._product = await response.json();
        return this._product;
      } catch (error) {
        console.error('Failed to load product data:', error);
        return null;
      }
    })();

    return this._productPromise;
  }

  _handleUrlVariant() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    
    if (variantId && this._masterSelect) {
      const optionExists = Array.from(this._masterSelect.options).some(
        opt => opt.value === variantId
      );
      
      if (optionExists && this._masterSelect.value !== variantId) {
        this._masterSelect.value = variantId;
        this._currentVariantId = variantId;
        this.setAttribute('variant', variantId);
      }
    }
  }

  _handleMasterSelectChange(e) {
    const variantId = e.target.value;
    this._currentVariantId = variantId;
    this.setAttribute('variant', variantId);

    // Dispatch variant-change event for empire.js compatibility
    this._dispatchVariantChange(variantId);
  }

  _updateUrl(variantId) {
    if (!variantId || variantId === 'not-selected') {
      return;
    }

    const currentUrl = new URL(window.location.href);
    if (variantId && variantId !== 'not-selected') {
      currentUrl.searchParams.set('variant', variantId);
    } else {
      currentUrl.searchParams.delete('variant');
    }

    if (window.location.href !== currentUrl.href) {
      history.replaceState({ path: currentUrl.href }, '', currentUrl.href);
    }
  }

  _dispatchVariantChange(variantId) {
    if (!variantId || variantId === 'not-selected') return;

    // Get the variant object
    this.getVariant().then(variant => {
      if (variant) {
        this.dispatchEvent(new CustomEvent('variant-change', {
          detail: { variant },
          bubbles: true
        }));
      }
    });
  }

  _updateGallery(variantId) {
    if (!variantId || variantId === 'not-selected' || variantId === 'unavailable' || !this._product) {
      console.log('Gallery update skipped:', { variantId, hasProduct: !!this._product });
      return;
    }

    // Skip gallery update if we're already updating from gallery to prevent loops
    if (this._updatingFromGallery) {
      console.log('Gallery update skipped - already updating from gallery');
      return;
    }

    const variant = this._product.variants.find(v => v.id.toString() === variantId);

    if (!variant) {
      console.log('Variant not found:', variantId);
      return;
    }

    // Try multiple ways to find the Splide carousel
    let mainCarousel = window.mainProductSplide;

    if (!mainCarousel) {
      // Try to find it by looking for splide instances
      const splideElements = document.querySelectorAll('.splide');
      for (const splideEl of splideElements) {
        if (splideEl.splide) {
          mainCarousel = splideEl.splide;
          break;
        }
      }
    }

    if (!mainCarousel) {
      console.log('Splide carousel not available');
      return;
    }

    // Use requestAnimationFrame for smoother updates
    requestAnimationFrame(() => {
      // Use Splide API to get the actual slides array
      const slides = mainCarousel.Components.Slides.get();

      if (!slides || slides.length === 0) {
        console.log('No slides found from Splide');
        return;
      }

      console.log('Looking for variant ID:', variantId);

      for (let i = 0; i < slides.length; i++) {
        const slideElement = slides[i].slide;
        const slideMediaId = slideElement?.dataset.mediaId;

        // Skip slides without media ID (e.g., featured image)
        if (!slideMediaId) {
          continue;
        }

        // Handle comma-separated variant IDs (multiple variants sharing same image)
        const mediaIds = slideMediaId.split(',').map(id => id.trim());

        if (mediaIds.includes(variantId)) {
          console.log('Found matching slide at index:', i);
          
          // Set flag to prevent circular updates
          if (window.gallerySync) {
            window.gallerySync.setUpdatingFromVariant(true);
          }
          
          mainCarousel.go(i);
          break;
        }
      }
    });
  }

  // Public API
  async getProduct() {
    // If product is already loaded, return it
    if (this._product) {
      return this._product;
    }

    // If loading is in progress, wait for it
    if (this._productPromise) {
      return await this._productPromise;
    }

    // Start loading and wait for it
    await this._loadProductData();
    return this._product;
  }

  get variant() {
    return this._currentVariantId;
  }

  set variant(value) {
    this._currentVariantId = value;
    if (this._masterSelect && this._masterSelect.value !== value) {
      this._masterSelect.value = value;
    }
    this.setAttribute('variant', value);
  }

  // Method expected by empire.js
  async getVariant() {
    if (!this._product) {
      await this._loadProductData();
    }

    if (!this._product || !this._currentVariantId) {
      return null;
    }

    return this._product.variants.find(variant => variant.id.toString() === this._currentVariantId.toString()) || null;
  }
}

// ===================================================================
// Options Selection Web Component
// ===================================================================
class OptionsSelection extends HTMLElement {
  static get observedAttributes() {
    return ['variant-selection', 'disable-unavailable', 'remove-unavailable'];
  }

  constructor() {
    super();
    this._variantSelection = null;
    this._selectOptions = [];
    this._radioOptions = [];
    this._initialized = false;
    this._onChangeBound = this._handleOptionChange.bind(this);
  }

  async connectedCallback() {
    // Find associated variant-selection
    const variantSelectionId = this.getAttribute('variant-selection');
    this._variantSelection = variantSelectionId
      ? document.getElementById(variantSelectionId)
      : this.closest('variant-selection');

    // Gather all option controls
    this._gatherOptions();

    // Attach event listeners
    this._attachListeners();

    // Initial update - retry if product not loaded yet
    await this._updateOptionsWithRetry();

    this._initialized = true;

    // Dispatch event to notify that options selection is ready
    this.dispatchEvent(new CustomEvent('options-selection:ready', {
      bubbles: true,
      detail: { initialized: true }
    }));
  }

  disconnectedCallback() {
    this._detachListeners();
  }

  _gatherOptions() {
    // Gather select options
    const selects = this.querySelectorAll('select[data-variant-option]');
    this._selectOptions = Array.from(selects).map(select => ({
      element: select,
      index: parseInt(select.dataset.variantOptionIndex || 0),
      name: select.dataset.selectOptionName || select.dataset.optionName || ''
    }));

    // Gather radio options
    const radioGroups = this.querySelectorAll('[data-variant-option]:not(select)');
    this._radioOptions = Array.from(radioGroups).map(group => {
      const radios = group.querySelectorAll('input[type="radio"][data-variant-option-value]');
      return {
        element: group,
        radios: Array.from(radios),
        index: parseInt(group.dataset.variantOptionIndex || 0),
        name: radios[0]?.dataset.selectOptionName || ''
      };
    });
  }

  _attachListeners() {
    // Listen to selects
    this._selectOptions.forEach(({ element }) => {
      element.addEventListener('change', this._onChangeBound);
    });

    // Listen to radios
    this._radioOptions.forEach(({ radios }) => {
      radios.forEach(radio => {
        radio.addEventListener('change', this._onChangeBound);
      });
    });
  }

  _detachListeners() {
    this._selectOptions.forEach(({ element }) => {
      element.removeEventListener('change', this._onChangeBound);
    });

    this._radioOptions.forEach(({ radios }) => {
      radios.forEach(radio => {
        radio.removeEventListener('change', this._onChangeBound);
      });
    });
  }

  async _handleOptionChange(e) {
    const selectedOptions = this._getSelectedOptions();

    // Update master select
    await this._updateMasterSelect(selectedOptions);

    // Update available options
    await this._updateOptions();

    // Handle cascading selects
    if (e.target.matches('select[data-variant-option-index]')) {
      this._handleCascadingSelect(e.target);
    }

    // Update labels
    this._updateLabels();

    // Ensure URL is updated when options change
    if (this._variantSelection) {
      const product = await this._variantSelection.getProduct();
      if (product) {
        const variant = this._findMatchingVariant(product.variants, selectedOptions);
        if (variant) {
          this._variantSelection._updateUrl(variant.id.toString());
        }
      }
    }
  }

  _getSelectedOptions() {
    const options = [];

    // Get selected values from selects
    this._selectOptions.forEach(({ element, index }) => {
      const value = element.value;
      options[index] = value !== 'not-selected' ? value : null;
    });

    // Get selected values from radios
    this._radioOptions.forEach(({ radios, index }) => {
      const checked = radios.find(r => r.checked);
      if (checked) {
        options[index] = checked.value;
      } else {
        options[index] = null;
      }
    });

    return options;
  }

  async _updateMasterSelect(selectedOptions) {
    if (!this._variantSelection) return;

    const product = await this._variantSelection.getProduct();
    if (!product) return;

    const variant = this._findMatchingVariant(product.variants, selectedOptions);

    if (variant) {
      // Sync option fields with the variant's actual values
      this._syncOptionFieldsWithVariant(variant);

      // Use the setter which will trigger URL update and events
      this._variantSelection.variant = variant.id.toString();
      this._updateAddToCartButton(variant.available);

      // Force visual update of select elements by triggering a reflow
      this._forceSelectVisualUpdate();
    } else {
      const hasUnselected = selectedOptions.some(opt => !opt);
      this._variantSelection.variant = hasUnselected ? 'not-selected' : 'unavailable';
      this._updateAddToCartButton(false);
    }
  }

  _syncOptionFieldsWithVariant(variant) {
    // Get variant options
    const variantOptions = [variant.option1, variant.option2, variant.option3];

    // Update select elements
    this._selectOptions.forEach(({ element, index }) => {
      const optionValue = variantOptions[index];
      if (optionValue && element.value !== optionValue) {
        // Check if this value exists in the select options
        const optionExists = Array.from(element.options).some(opt => opt.value === optionValue);
        if (optionExists) {
          element.value = optionValue;
        }
      }
    });

    // Update radio elements
    this._radioOptions.forEach(({ radios, index }) => {
      const optionValue = variantOptions[index];
      if (optionValue) {
        radios.forEach(radio => {
          radio.checked = radio.value === optionValue;
        });
      }
    });
  }

  _forceSelectVisualUpdate() {
    // Force browser to update select element display by triggering a reflow
    this._selectOptions.forEach(({ element }) => {
      const display = element.style.display;
      element.style.display = 'none';
      element.offsetHeight; // Trigger reflow
      element.style.display = display;
    });
  }

  _findMatchingVariant(variants, selectedOptions) {
    return variants.find(variant => {
      const variantOptions = [variant.option1, variant.option2, variant.option3];
      
      return selectedOptions.every((selectedOption, index) => {
        if (!selectedOption) return true; // Skip unselected options
        return selectedOption === variantOptions[index];
      });
    });
  }

  _updateAddToCartButton(isAvailable) {
    const addToCartButton = document.querySelector('.product-form--atc-button');
    if (!addToCartButton) return;

    if (isAvailable) {
      addToCartButton.removeAttribute('disabled');
      addToCartButton.classList.remove('disabled');
    } else {
      addToCartButton.setAttribute('disabled', 'disabled');
      addToCartButton.classList.add('disabled');
    }
  }

  async _updateOptionsWithRetry() {
    if (!this._variantSelection) return;

    try {
      const product = await this._variantSelection.getProduct();
      if (product) {
        // Product is loaded, update options
        this._updateOptions();
      }
    } catch (error) {
      console.error('Failed to load product for options update:', error);
    }
  }

  async _updateOptions() {
    if (!this._variantSelection) return;

    const product = await this._variantSelection.getProduct();
    if (!product) return;

    const selectedOptions = this._getSelectedOptions();
    const disableUnavailable = this.hasAttribute('disable-unavailable');
    const removeUnavailable = this.hasAttribute('remove-unavailable');

    // Update select dropdowns
    this._selectOptions.forEach(({ element, index }) => {
      const options = Array.from(element.querySelectorAll('option'));
      
      options.forEach(option => {
        if (option.value === 'not-selected') return;
        
        const testOptions = [...selectedOptions];
        testOptions[index] = option.value;
        
        const isAvailable = this._isVariantAvailable(product.variants, testOptions);
        
        option.dataset.variantOptionAvailable = isAvailable;
        
        if (disableUnavailable) {
          option.disabled = !isAvailable;
        }
        
        if (removeUnavailable && !isAvailable) {
          option.style.display = 'none';
        } else {
          option.style.display = '';
        }
      });
    });

    // Update radio options
    this._radioOptions.forEach(({ radios, index }) => {
      radios.forEach(radio => {
        const testOptions = [...selectedOptions];
        testOptions[index] = radio.value;
        
        const isAvailable = this._isVariantAvailable(product.variants, testOptions);
        
        radio.dataset.variantOptionAvailable = isAvailable;
        radio.parentElement.dataset.variantOptionAvailable = isAvailable;
        
        if (disableUnavailable) {
          radio.disabled = !isAvailable;
        }
        
        if (removeUnavailable && !isAvailable) {
          radio.parentElement.style.display = 'none';
        } else {
          radio.parentElement.style.display = '';
        }
      });
    });
  }

  _isVariantAvailable(variants, testOptions) {
    return variants.some(variant => {
      const variantOptions = [variant.option1, variant.option2, variant.option3];
      
      const matches = testOptions.every((testOption, index) => {
        if (!testOption) return true;
        return testOption === variantOptions[index];
      });
      
      return matches && variant.available;
    });
  }

  _handleCascadingSelect(selectElement) {
    const optionIndex = parseInt(selectElement.dataset.variantOptionIndex);
    const nextOptionIndex = optionIndex + 1;
    const nextSelect = this.querySelector(
      `select[data-variant-option-index="${nextOptionIndex}"]`
    );

    if (nextSelect && nextSelect.value === 'not-selected') {
      const firstAvailableOption = nextSelect.querySelector(
        'option:not([disabled]):not([value="not-selected"])'
      );
      
      if (firstAvailableOption) {
        nextSelect.value = firstAvailableOption.value;
        nextSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  _updateLabels() {
    const unavailableText = this.getAttribute('data-unavailable-text');
    const selectedOptions = this._getSelectedOptions();

    // Update option headers/labels
    this._selectOptions.forEach(({ element, index }) => {
      const label = element.closest('.options-selection__select-wrapper')
        ?.querySelector('[data-variant-option-name]');

      if (label) {
        const value = selectedOptions[index];
        label.dataset.variantOptionChosenValue = value || 'false';

        if (value && value !== 'not-selected') {
          // Find the selected option and use its text
          const selectedOption = element.querySelector(`option[value="${CSS.escape(value)}"]`);
          if (selectedOption) {
            label.textContent = selectedOption.textContent;
          } else {
            label.textContent = element.dataset.selectOptionName || element.name;
          }
        } else {
          label.textContent = label.dataset.variantOptionChooseName ||
            `Choose ${element.dataset.selectOptionName || element.name}`;
        }
      }

      // Update option text with unavailable indicator
      if (!this.hasAttribute('disable-unavailable') && unavailableText) {
        const options = element.querySelectorAll('option');
        options.forEach(option => {
          if (option.value === 'not-selected') return;

          let optionLabel = option.textContent.replace(` - ${unavailableText}`, '');

          if (option.dataset.variantOptionAvailable === 'false') {
            optionLabel = `${optionLabel} - ${unavailableText}`;
          }

          option.textContent = optionLabel;
        });
      }
    });

    // Update radio option headers
    this._radioOptions.forEach(({ element, index }) => {
      const legend = element.closest('fieldset')
        ?.querySelector('[data-variant-option-name]');
      
      if (legend) {
        const value = selectedOptions[index];
        legend.dataset.variantOptionChosenValue = value || 'false';
        
        if (value) {
          const chosenHtml = legend.dataset.variantOptionName || '';
          legend.innerHTML = chosenHtml;
          
          const valueSpan = legend.querySelector('.option-swatch-value, span');
          if (valueSpan) {
            valueSpan.textContent = value;
          }
        } else {
          legend.textContent = legend.dataset.variantOptionChooseName || 
            `Choose option`;
        }
      }
    });
  }
}

// ===================================================================
// Gallery Slide Change Handler
// ===================================================================
class GalleryThumbnailSync {
  constructor() {
    this._initialized = false;
    this._mainCarousel = null;
    this._updatingFromVariant = false;
  }

  init() {
    if (this._initialized) return;

    document.addEventListener('DOMContentLoaded', () => {
      this._setupGalleryListeners();
    });

    this._initialized = true;
  }

  setUpdatingFromVariant(value) {
    this._updatingFromVariant = value;
  }

  _setupGalleryListeners() {
    // Wait for Splide to be initialized
    const checkForSplide = () => {
      let mainCarousel = window.mainProductSplide;

      if (!mainCarousel) {
        // Try to find it by looking for splide instances
        const splideElements = document.querySelectorAll('.splide');
        for (const splideEl of splideElements) {
          if (splideEl.splide) {
            mainCarousel = splideEl.splide;
            break;
          }
        }
      }

      if (mainCarousel) {
        this._mainCarousel = mainCarousel;
        this._attachSplideListeners();
      } else {
        // Retry after a short delay
        setTimeout(checkForSplide, 100);
      }
    };

    checkForSplide();
  }

  _attachSplideListeners() {
    if (!this._mainCarousel) return;

    // Listen for slide changes
    this._mainCarousel.on('moved', (newIndex, prevIndex, destIndex) => {
      this._handleSlideChange(newIndex);
    });
  }

  _handleSlideChange(slideIndex) {
    if (!this._mainCarousel) return;

    // Skip select updates if we're updating from variant changes to prevent loops
    if (this._updatingFromVariant) {
      console.log('Slide change skipped - updating from variant');
      // Reset the flag after a short delay
      setTimeout(() => {
        this._updatingFromVariant = false;
      }, 50);
      return;
    }

    // Account for featured image at index 0
    const slides = this._mainCarousel.Components.Slides.get();
    if (!slides || slides.length === 0 || slideIndex >= slides.length) return;

    const currentSlide = slides[slideIndex].slide;
    if (!currentSlide) return;

    // Get variant options from the slide's data attributes
    const option1Value = currentSlide.dataset.variantOptions1?.split(',')[0];
    const option2Value = currentSlide.dataset.variantOptions2?.split(',')[0];
    const option3Value = currentSlide.dataset.variantOptions3?.split(',')[0];

    // Update the options selection to match this slide's variant options
    const optionsSelection = document.querySelector('options-selection');
    if (!optionsSelection) return;

    // Set flag to prevent gallery updates from these select changes
    const variantSelection = document.querySelector('variant-selection');
    if (variantSelection && variantSelection._updatingFromGallery !== undefined) {
      variantSelection._updatingFromGallery = true;
    }

    // Update selects
    const optionSelects = optionsSelection.querySelectorAll('select[data-variant-option]');
    if (option1Value && optionSelects.length > 0) {
      const select = optionSelects[0];
      if (select.value !== option1Value) {
        // Check if this value exists in the select options
        const optionExists = Array.from(select.options).some(opt => opt.value === option1Value);
        if (optionExists) {
          select.value = option1Value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    if (option2Value && optionSelects.length > 1) {
      const select = optionSelects[1];
      if (select.value !== option2Value) {
        const optionExists = Array.from(select.options).some(opt => opt.value === option2Value);
        if (optionExists) {
          select.value = option2Value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    if (option3Value && optionSelects.length > 2) {
      const select = optionSelects[2];
      if (select.value !== option3Value) {
        const optionExists = Array.from(select.options).some(opt => opt.value === option3Value);
        if (optionExists) {
          select.value = option3Value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // Update radio buttons
    const radioGroups = optionsSelection.querySelectorAll('[data-variant-option]:not(select)');
    radioGroups.forEach((group, index) => {
      const optionValue = [option1Value, option2Value, option3Value][index];
      if (optionValue) {
        const radios = group.querySelectorAll('input[type="radio"][data-variant-option-value]');
        radios.forEach(radio => {
          if (radio.value === optionValue && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    });

    // Reset the flag after select updates are complete
    setTimeout(() => {
      if (variantSelection && variantSelection._updatingFromGallery !== undefined) {
        variantSelection._updatingFromGallery = false;
      }
    }, 50);
  }

  _updateOption(optionInput, optionValue) {
    if (!optionInput || !optionValue) return;

    const optionVal = optionValue.includes(',') ? optionValue.split(',')[0] : optionValue;
    
    // Handle select dropdowns
    if (optionInput.tagName === 'SELECT') {
      if (optionInput.value !== optionVal) {
        optionInput.value = optionVal;
        optionInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } 
    // Handle radio button groups (swatches or regular radios)
    else if (optionInput.querySelector('.options-selection__option-value-input')) {
      const radios = optionInput.querySelectorAll('.options-selection__option-value-input');
      radios.forEach(radio => {
        if (radio.value === optionVal && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }
}

// ===================================================================
// Register Web Components
// ===================================================================
// Note: empire.min.js contains old versions of these components.
// Due to custom element limitations, we can't unregister them.
// This registration will succeed if empire.min.js hasn't loaded yet (due to async),
// or will be skipped if the old ones are already registered.
// To ensure the new components are used, options-selection.js should load first.

// Since empire.min.js loads first and defines the components, we need to enhance
// the existing components rather than replace them

document.addEventListener('DOMContentLoaded', () => {
  // Find existing variant-selection elements and enhance them
  const variantSelectionElements = document.querySelectorAll('variant-selection');
  variantSelectionElements.forEach(element => {
    enhanceVariantSelection(element);
  });

  // Find existing options-selection elements and enhance them
  const optionsSelectionElements = document.querySelectorAll('options-selection');
  optionsSelectionElements.forEach(element => {
    enhanceOptionsSelection(element);
  });

  // Also watch for new elements being added
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          if (node.tagName === 'VARIANT-SELECTION') {
            enhanceVariantSelection(node);
          } else if (node.tagName === 'OPTIONS-SELECTION') {
            enhanceOptionsSelection(node);
          }
          // Also check child elements
          const variantSelections = node.querySelectorAll ? node.querySelectorAll('variant-selection') : [];
          const optionsSelections = node.querySelectorAll ? node.querySelectorAll('options-selection') : [];
          variantSelections.forEach(el => enhanceVariantSelection(el));
          optionsSelections.forEach(el => enhanceOptionsSelection(el));
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});

function enhanceVariantSelection(element) {
  console.log('🔧 Enhancing variant-selection element');

  // Add our methods to the existing element
  if (!element._enhanced) {
    // Copy our methods
    element._loadProductData = VariantSelection.prototype._loadProductData;
    element._handleUrlVariant = VariantSelection.prototype._handleUrlVariant;
    element._handleMasterSelectChange = VariantSelection.prototype._handleMasterSelectChange;
    element._updateUrl = VariantSelection.prototype._updateUrl;
    element._updateGallery = VariantSelection.prototype._updateGallery;
    element._dispatchVariantChange = VariantSelection.prototype._dispatchVariantChange;
    element.getProduct = VariantSelection.prototype.getProduct;
    element.getVariant = VariantSelection.prototype.getVariant;

    // Add our properties
    element._product = null;
    element._productUrl = element.getAttribute('product-url') || '';
    element._currentVariantId = element.getAttribute('variant') || null;
    element._initialized = true;
    element._updatingFromGallery = false;

    // Initialize
    element._loadProductData();
    element._handleUrlVariant();

    // Override the variant setter
    const originalSetAttribute = element.setAttribute.bind(element);
    element.setAttribute = function(name, value) {
      originalSetAttribute(name, value);
      if (name === 'variant') {
        this._currentVariantId = value;
        this._updateUrl(value);
        this._updateGallery(value);
        this._dispatchVariantChange(value);
      }
    };

    element._enhanced = true;
    console.log('✓ variant-selection element enhanced');
  }
}

function enhanceOptionsSelection(element) {
  console.log('🔧 Enhancing options-selection element');

  if (!element._enhanced) {
    // Copy our methods
    element._gatherOptions = OptionsSelection.prototype._gatherOptions;
    element._attachListeners = OptionsSelection.prototype._attachListeners;
    element._detachListeners = OptionsSelection.prototype._detachListeners;
    element._handleOptionChange = OptionsSelection.prototype._handleOptionChange;
    element._getSelectedOptions = OptionsSelection.prototype._getSelectedOptions;
    element._updateMasterSelect = OptionsSelection.prototype._updateMasterSelect;
    element._findMatchingVariant = OptionsSelection.prototype._findMatchingVariant;
    element._updateAddToCartButton = OptionsSelection.prototype._updateAddToCartButton;
    element._updateOptionsWithRetry = OptionsSelection.prototype._updateOptionsWithRetry;
    element._updateOptions = OptionsSelection.prototype._updateOptions;
    element._isVariantAvailable = OptionsSelection.prototype._isVariantAvailable;
    element._handleCascadingSelect = OptionsSelection.prototype._handleCascadingSelect;
    element._updateLabels = OptionsSelection.prototype._updateLabels;

    // Initialize
    element._variantSelection = element.getAttribute('variant-selection')
      ? document.getElementById(element.getAttribute('variant-selection'))
      : element.closest('variant-selection');

    element._selectOptions = [];
    element._radioOptions = [];
    element._initialized = true;

    element._gatherOptions();
    element._attachListeners();
    element._updateOptionsWithRetry();

    element._enhanced = true;
    console.log('✓ options-selection element enhanced');
  }
}

// Define our components as fallbacks if they haven't been defined yet
if (!customElements.get('variant-selection')) {
  customElements.define('variant-selection', VariantSelection);
  console.log('✓ New VariantSelection web component registered');
} else {
  console.log('ℹ️ variant-selection already registered, using enhancement approach');
}

if (!customElements.get('options-selection')) {
  customElements.define('options-selection', OptionsSelection);
  console.log('✓ New OptionsSelection web component registered');
} else {
  console.log('ℹ️ options-selection already registered, using enhancement approach');
}

// ===================================================================
// Initialize Gallery Sync
// ===================================================================
const gallerySync = new GalleryThumbnailSync();
gallerySync.init();

// Make gallery sync globally available for VariantSelection to use
window.gallerySync = gallerySync;

// ===================================================================
// Stock Checker Integration
// ===================================================================
document.addEventListener('DOMContentLoaded', () => {
  function checkForStock() {
    const escBlock = document.querySelector('#esc-oos-form');
    const submitButton = document.querySelector('.product-form--atc-button');

    if (!submitButton || !escBlock) return;

    const isUnavailable = submitButton.disabled || submitButton.hasAttribute('disabled');
    escBlock.style.display = isUnavailable ? 'block' : 'none';
  }

  function attachVariantListeners() {
    const selects = document.querySelectorAll('select[data-variant-option]');
    selects.forEach(select => {
      select.addEventListener('change', () => {
        setTimeout(checkForStock, 100);
      });
    });

    const radios = document.querySelectorAll('input[type="radio"][data-variant-option-value]');
    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        setTimeout(checkForStock, 100);
      });
    });
  }

  function initMutationObserver() {
    const observer = new MutationObserver(() => {
      checkForStock();
    });

    const submitButton = document.querySelector('.product-form--atc-button');
    if (submitButton) {
      observer.observe(submitButton, {
        attributes: true,
        attributeFilter: ['disabled']
      });
    }
  }

  function initOptionsSelectionListener() {
    // Listen for when options selection is ready
    document.addEventListener('options-selection:ready', () => {
      // Run stock check after options selection has initialized
      setTimeout(checkForStock, 50);
    });
  }

  // Initial setup
  attachVariantListeners();
  initMutationObserver();
  initOptionsSelectionListener();

  // Initial check (may run before options selection is ready)
  checkForStock();
});
